import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import {
  Modal,
  Stack,
  Text,
  Box,
  Button,
  useTheme,
  IconButton,
} from '@ltvco/refresh-lib/theme';
import { useScreenSize } from '@ltvco/refresh-lib/utils';
import CloseIcon from '@mui/icons-material/Close';
import { useState, useEffect } from 'react';

import { HELP_MODAL_ANIMATIONS } from 'components/ChatBV/constants';

type HelpModalProps = {
  open: boolean;
  onClose: () => void;
  // Optional: DeepSearch passes this to start a new chat; ChatBV omits it (close-only).
  onStartSearching?: () => void;
};

export const HelpModal = ({
  open,
  onClose,
  onStartSearching,
}: HelpModalProps) => {
  const [currentAnimation, setCurrentAnimation] = useState(0);
  const [key, setKey] = useState(0);
  const theme = useTheme();
  const { isMobile } = useScreenSize();

  // Reset animation sequence when modal opens
  useEffect(() => {
    if (open) {
      setCurrentAnimation(0);
      setKey((prev) => prev + 1);
    }
  }, [open]);

  // Handle animation sequence timing
  useEffect(() => {
    if (!open) {
      return;
    }

    const currentAnimationData = HELP_MODAL_ANIMATIONS[currentAnimation];
    const timer = setTimeout(() => {
      if (currentAnimation < HELP_MODAL_ANIMATIONS.length - 1) {
        // Move to next animation
        setCurrentAnimation(currentAnimation + 1);
        setKey((prev) => prev + 1);
      } else {
        // All animations played, restart the sequence
        setCurrentAnimation(0);
        setKey((prev) => prev + 1);
      }
    }, currentAnimationData.duration);

    return () => clearTimeout(timer);
  }, [currentAnimation, open, HELP_MODAL_ANIMATIONS]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDialogContent-root': {
          padding: '0',
        },

        '& .MuiDialog-paper': {
          borderRadius: isMobile ? '0' : '16px',
          margin: isMobile ? '0' : 'auto',
          height: isMobile ? '100vh' : 'auto',
          maxHeight: isMobile ? 'none' : 'inherit',
          maxWidth: isMobile ? 'none' : '800px',
        },
      }}
    >
      <Stack direction={isMobile ? 'column-reverse' : 'row'}>
        <Stack
          spacing={isMobile ? 3 : 4}
          width={isMobile ? '100%' : '50%'}
          sx={{
            py: 2,
            px: isMobile ? 2 : 4,
            height: isMobile ? '55vh' : 'auto',
            maxWidth: isMobile ? '100%' : '50%',
            marginTop: isMobile ? '0' : theme.spacing(2),
          }}
          justifyContent="center"
        >
          <Box>
            <Text
              sx={{
                color: theme.palette.primary.main,
                fontSize: 12,
                fontWeight: 600,
                backgroundColor: theme.palette.primary.light,
                padding: 1,
                borderRadius: 4,
                width: 'fit-content',
                marginBottom: 1,
              }}
            >
              NEW
            </Text>
            <Text variant="h2" sx={{ mb: 1 }}>
              Introducing Deep Search
            </Text>
            <Text sx={{ fontSize: 14, color: theme.palette.text.secondary }}>
              A new way to search on BeenVerified.
            </Text>
          </Box>
          <Box>
            <Text variant="h4" sx={{ mb: 1 }}>
              Lookup info about people and places
            </Text>
            <Text sx={{ fontSize: 14, color: theme.palette.text.secondary }}>
              Search through millions of records via chat. Try asking questions
              about phone numbers, emails, people and more.
            </Text>
          </Box>
          <Box>
            <Text variant="h4" sx={{ mb: 1 }}>
              See detailed reports
            </Text>
            <Text sx={{ fontSize: 14, color: theme.palette.text.secondary }}>
              Go deeper on reports that really interest you. Try asking for an
              in-depth analysis on the things that you search for.
            </Text>
          </Box>
          <Stack
            direction="row"
            justifyContent="space-between"
            sx={{ pt: isMobile ? 8 : 6 }}
          >
            <Button
              variant="outlined"
              sx={{
                border: 'none',
                color: theme.palette.text.secondary,
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: 'transparent',
                  border: 'none',
                },
              }}
              onClick={onClose}
            >
              <Text
                sx={{
                  fontWeight: 'bold',
                  color: theme.palette.text.primary,
                  fontSize: 14,
                }}
              >
                Cancel
              </Text>
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                onClose();
                onStartSearching?.();
              }}
              sx={{ borderRadius: 5 }}
            >
              <Text
                sx={{
                  fontSize: 14,
                  fontWeight: 'bold',
                  py: 0.5,
                  px: 6,
                }}
              >
                Start Searching
              </Text>
            </Button>
          </Stack>
        </Stack>
        <DotLottieReact
          key={key}
          src={HELP_MODAL_ANIMATIONS[currentAnimation].src}
          loop={false}
          autoplay
          style={{
            width: isMobile ? '100%' : '50%',
            height: isMobile ? '45vh' : '500px',
            background:
              'linear-gradient(347deg, #D2FFD4 0%, rgba(255, 238.35, 224.61, 0.30) 27%, #E1DBFF 51%, #C5D8FF 76%)',
          }}
        />
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: theme.spacing(1),
            right: theme.spacing(1),
          }}
        >
          <CloseIcon />
        </IconButton>
      </Stack>
    </Modal>
  );
};
